import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useAppSelector } from 'store';
import { selectIsDropshipSetup } from 'store/selectors/me/company';
import Menu from 'storybookConfig/stories/cells/Menu';
import Badge from 'storybookConfig/stories/molecules/Badge';
import { listTradeOpsIssues } from 'utils/api/issues';
import { useAnnouncementToDisplay } from './Announcements';
import Body from 'storybookConfig/stories/molecules/Body';
import Collapse, { CollapseChevron } from 'storybookConfig/stories/molecules/Collapse';
import { SubLinkContainer } from './Shared.styles';
import useFlagEnabled from 'hooks/useFlagEnabled';

const FETCHING_TRADE_OPS_ISSUES_QUERY_KEY = [
  'listTradeOpsIssues',
  {
    page: 0,
    limit: 100,
    archived: false,
    severityLevels: ['error', 'critical'],
  },
] as const;

interface FooterMenuProps {
  isActive: (path: string) => boolean;
}

const FooterMenu = ({ isActive }: FooterMenuProps) => {
  const isDropshipSetup = useAppSelector(selectIsDropshipSetup);
  const announcementToDisplay = useAnnouncementToDisplay();
  const [expanded, setExpanded] = useState(!announcementToDisplay);
  const settingsId = useId();
  const showMarketplace = useFlagEnabled('showMarketplace');

  /**
   * Queries
   */

  const fetchingTradeOpsIssues = useQuery({
    queryKey: FETCHING_TRADE_OPS_ISSUES_QUERY_KEY,
    queryFn: ({ queryKey }) => listTradeOpsIssues(queryKey[1]),
    enabled: isDropshipSetup,
  });

  const tradeOpsIssues = fetchingTradeOpsIssues.data?.data?.errors ?? [];

  /**
   * Render
   */

  if (!showMarketplace) {
    // Render the original links directly, no dropdown
    return (
      <Menu>
        {isDropshipSetup && (
          <Menu.Link to="/issues" $iconName="circle-alert" $isActive={isActive('/issues')}>
            Issues
            <Badge variant="info" count={tradeOpsIssues.length} data-testid="issue-count-badge" />
          </Menu.Link>
        )}
        <Menu.Link
          to="#"
          as="a"
          $iconName="list"
          href="https://moderndropship.frontkb.com/en"
          rel="noopener noreferrer"
          target="_blank"
        >
          Help Center
        </Menu.Link>
        <Menu.Link
          to="#"
          as="a"
          $iconName="headset"
          href="https://moderndropship.frontkb.com/en/contact-us"
          rel="noopener noreferrer"
          target="_blank"
        >
          Submit an Issue
        </Menu.Link>
        <Menu.Link to="/settings" $iconName="settings">
          Settings
        </Menu.Link>
      </Menu>
    );
  }

  // New dropdown UI for flagged users
  return (
    <Menu>
      <>
        <Menu.LinkButton
          aria-expanded={expanded}
          aria-controls={settingsId}
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <Body variant="Body/Header">Settings</Body>
          <CollapseChevron open={expanded} />
        </Menu.LinkButton>
        <Collapse id={settingsId} open={expanded}>
          <SubLinkContainer>
            {isDropshipSetup && (
              <Menu.Link to="/issues" $iconName="circle-alert" $isActive={isActive('/issues')}>
                Issues
                <Badge
                  variant="info"
                  count={tradeOpsIssues.length}
                  data-testid="issue-count-badge"
                />
              </Menu.Link>
            )}
            <Menu.Link
              to="#"
              as="a"
              $iconName="list"
              href="https://moderndropship.frontkb.com/en"
              rel="noopener noreferrer"
              target="_blank"
            >
              Help Center
            </Menu.Link>
            <Menu.Link
              to="#"
              as="a"
              $iconName="headset"
              href="https://moderndropship.frontkb.com/en/contact-us"
              rel="noopener noreferrer"
              target="_blank"
            >
              Submit an Issue
            </Menu.Link>
            <Menu.Link to="/settings" $iconName="settings">
              Settings
            </Menu.Link>
          </SubLinkContainer>
        </Collapse>
      </>
    </Menu>
  );
};

export default FooterMenu;
